import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = API_BASE;

const PermissionContext = createContext(null);
const normalizeRole = (role) => String(role || '').toLowerCase();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {const { t } = useTranslation("common");
  const { user, isAuthenticated } = useAuth();
  const userRole = normalizeRole(user?.role);
  const [permissions, setPermissions] = useState({
    allowed: [],
    denied: [],
    profiles: [],
    loading: true,
    error: null
  });

  // Fetch user permissions from API
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setPermissions({ allowed: [], denied: [], profiles: [], loading: false, error: null });
      return;
    }

    setPermissions({ allowed: [], denied: [], profiles: [], loading: true, error: null });

    try {
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/access-profiles/users/${user.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPermissions({
          allowed: data.allowed || [],
          denied: data.denied || [],
          profiles: data.profiles || [],
          loading: false,
          error: null
        });
      } else {
        setPermissions({
          allowed: [],
          denied: [],
          profiles: [],
          loading: false,
          error: `Unable to load profile permissions (${response.status})`
        });
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions({
        allowed: [],
        denied: [],
        profiles: [],
        loading: false,
        error: error.message
      });
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((capabilityCode) => {
    // Admin has all permissions
    if (userRole === 'admin') return true;

    if (permissions.loading) return false;

    // Check if explicitly denied
    if (permissions.denied.includes(capabilityCode)) return false;

    // Check if explicitly allowed
    if (permissions.allowed.includes(capabilityCode)) return true;

    // Check with wildcards (e.g., "wms.*" allows "wms.inventory.view")
    for (const allowed of permissions.allowed) {
      if (allowed.endsWith('.*')) {
        const prefix = allowed.slice(0, -2);
        if (capabilityCode.startsWith(prefix + '.')) return true;
      }
    }

    return false;
  }, [userRole, permissions.loading, permissions.allowed, permissions.denied]);

  // Check if user has ANY of the specified permissions
  const hasAnyPermission = useCallback((capabilityCodes) => {
    return capabilityCodes.some((code) => hasPermission(code));
  }, [hasPermission]);

  // Check if user has ALL of the specified permissions
  const hasAllPermissions = useCallback((capabilityCodes) => {
    return capabilityCodes.every((code) => hasPermission(code));
  }, [hasPermission]);

  const hasModulePermission = useCallback((moduleName) => {
    if (userRole === 'admin') return true;
    if (permissions.loading) return false;
    const prefix = `${moduleName}.`;
    return permissions.allowed.some((code) => code === `${moduleName}.*` || code.startsWith(prefix));
  }, [permissions.allowed, permissions.loading, userRole]);

  // Get all permissions for a module
  const getModulePermissions = useCallback((moduleName) => {
    const prefix = `${moduleName}.`;
    return {
      allowed: permissions.allowed.filter((p) => p.startsWith(prefix)),
      denied: permissions.denied.filter((p) => p.startsWith(prefix))
    };
  }, [permissions.allowed, permissions.denied]);

  // Check if user can access a route
  const canAccessRoute = useCallback((route) => {
    const unrestrictedRoutes = new Set([
      '/settings/user',
      '/wms/settings/user',
      '/profile',
      '/user/profile'
    ]);

    if (unrestrictedRoutes.has(route)) return true;

    const routePermissionMap = {
      // ERP shell/dashboard
      '/erp': ['admin.system.view'],

      // WMS routes
      '/wms': ['wms.dashboard.view'],
      '/wms/dashboard': ['wms.dashboard.view'],
      '/wms/inventory': ['wms.inventory.view'],
      '/wms/products': ['wms.inventory.view'],
      '/wms/receiving': ['wms.receiving.view'],
      '/wms/putaway': ['wms.putaway.view'],
      '/wms/picking': ['wms.picking.view'],
      '/wms/packing': ['wms.packing.view'],
      '/wms/pallets': ['wms.shipping.view'],
      '/wms/expedition': ['wms.shipping.view'],
      '/wms/adjustments': ['wms.inventory.view'],
      '/wms/scanner': ['wms.inventory.view'],
      '/wms/pharmascan': ['wms.inventory.view'],
      '/wms/transfers': ['wms.inventory.view'],
      '/wms/rush-orders': ['wms.shipping.view'],
      '/wms/returns': ['wms.returns.view'],
      '/wms/config': ['wms.config.view'],
      '/wms/reports': ['wms.dashboard.view'],
      '/wms/devices': ['wms.config.view'],
      '/wms/gps-tracking': ['geofencing.dashboard.view'],
      '/wms/temperature-alerts': ['coldchain.dashboard.view'],
      '/wms/geofencing': ['geofencing.dashboard.view'],
      '/wms/coldchain': ['coldchain.dashboard.view'],
      '/wms/notifications': ['wms.config.view'],
      '/wms/print': ['wms.config.view'],
      '/wms/documents': ['wms.dashboard.view'],
      '/wms/route-optimization': ['wms.shipping.view'],
      '/wms/trails': ['trails.dashboard.view'],
      '/wms/admin/access-profiles': ['admin.profiles.view'],
      '/wms/admin/system-settings': ['admin.system.view'],
      '/wms/admin/api-keys': ['admin.system.edit'],
      '/wms/admin/help': ['admin.system.view'],
      '/wms/settings/traffic': ['admin.system.view'],
      '/wms/settings/storage': ['admin.system.view'],

      // Admin routes
      '/admin/access-profiles': ['admin.profiles.view'],
      '/admin/system-settings': ['admin.system.view'],
      '/admin/api-keys': ['admin.system.edit'],
      '/admin/help-center': ['admin.system.view'],
      '/admin/help': ['admin.system.view'],
      '/admin/landing': ['admin.system.view'],
      '/help': ['admin.system.view'],

      // Finance routes
      '/finance': ['finance.dashboard.view'],
      '/finance/dashboard': ['finance.dashboard.view'],
      '/finance/general-ledger': ['finance.ledger.view'],
      '/finance/accounts-payable': ['finance.payable.view'],
      '/finance/accounts-receivable': ['finance.receivable.view'],
      '/finance/ar': ['finance.receivable.view'],
      '/finance/ap': ['finance.payable.view'],
      '/finance/payments': ['finance.dashboard.view'],
      '/finance/fixed-assets': ['finance.dashboard.view'],
      '/finance/tax': ['finance.dashboard.view'],
      '/finance/banking': ['finance.dashboard.view'],
      '/finance/reports': ['finance.reports.view', 'finance.dashboard.view'],
      '/finance/expenses': ['finance.dashboard.view'],
      '/finance/budgets': ['finance.dashboard.view'],
      '/finance/document-setup': ['finance.dashboard.view'],
      '/finance/bank-feeds': ['finance.dashboard.view'],

      // Procurement routes
      '/procurement': ['procurement.dashboard.view'],
      '/procurement/dashboard': ['procurement.dashboard.view'],
      '/procurement/vendors': ['procurement.vendors.view'],
      '/procurement/purchase-orders': ['procurement.po.view'],
      '/procurement/requisitions': ['procurement.po.view'],
      '/procurement/rfq': ['procurement.po.view'],
      '/procurement/goods-receipt': ['procurement.po.view'],
      '/procurement/supplier-returns': ['procurement.po.view'],
      '/procurement/payments': ['procurement.po.view'],
      '/procurement/reports': ['procurement.dashboard.view'],

      // Ecommerce routes
      '/ecommerce': ['ecommerce.dashboard.view'],
      '/ecommerce/dashboard': ['ecommerce.dashboard.view'],
      '/ecommerce/builder': ['ecommerce.store.manage'],
      '/ecommerce/stores': ['ecommerce.store.manage'],
      '/ecommerce/products': ['ecommerce.products.view'],
      '/ecommerce/orders': ['ecommerce.orders.view'],

      // CRM routes
      '/crm': ['crm.dashboard.view', 'crm.leads.view', 'crm.accounts.view', 'crm.opportunities.view', 'crm.campaign_intelligence.view'],
      '/crm/dashboard': ['crm.dashboard.view'],
      '/crm/leads': ['crm.leads.view'],
      '/crm/pipeline': ['crm.opportunities.view'],
      '/crm/opportunities': ['crm.opportunities.view'],
      '/crm/quotes': ['crm.quotes.view'],
      '/crm/accounts': ['crm.accounts.view'],
      '/crm/prospect-discovery': ['crm.leads.view'],
      '/crm/campaign-intelligence': ['crm.dashboard.view', 'crm.campaign_intelligence.view', 'crm.leads.view'],
      '/crm/marketing-hub': ['crm.leads.view'],
      '/crm/portal-manager': ['crm.dashboard.view', 'crm.portal_manager.view'],
      '/crm/orchestration': ['crm.dashboard.view', 'crm.orchestration.view'],
      '/crm/brand-protection': ['crm.dashboard.view', 'crm.brand_protection.view'],
      '/crm/publishing-console': ['crm.dashboard.view', 'crm.publishing_console.view'],
      '/crm/ad-connectors': ['crm.dashboard.view', 'crm.ad_connectors.view'],
      '/crm/presentation-hub': ['crm.dashboard.view', 'crm.presentation_hub.view'],
      '/crm/dropship': ['crm.accounts.view'],
      '/crm/consignment': ['crm.accounts.view'],
      '/crm/activities': ['crm.leads.view'],
      '/crm/reports': ['crm.dashboard.view'],

      // Communications routes
      '/communications': ['communications.dashboard.view'],
      '/communications/dashboard': ['communications.dashboard.view'],
      '/communications/templates': ['communications.templates.view'],
      '/communications/logs': ['communications.logs.view'],
      '/communications/telegram': ['communications.dashboard.view'],
      '/communications/whatsapp': ['communications.dashboard.view'],
      '/communications/sms': ['communications.send.sms'],
      '/communications/email': ['communications.send.email'],
      '/communications/rules': ['communications.dashboard.view'],

      // OIG routes
      '/oig': ['oig.dashboard.view'],
      '/oig/dashboard': ['oig.dashboard.view'],
      '/oig/nfe/config': ['oig.nfe.view'],
      '/oig/nfe/upload': ['oig.nfe.upload'],
      '/oig/nfe': ['oig.nfe.view'],
      '/oig/cte': ['oig.cte.view'],
      '/oig/edi': ['oig.dashboard.view'],
      '/oig/csv': ['oig.import.execute'],
      '/oig/asn': ['oig.asn.view'],
      '/oig/history': ['oig.dashboard.view'],

      // Calendar routes
      '/calendar': ['calendar.dashboard.view'],
      '/calendar/events': ['calendar.events.view'],
      '/calendar/rules': ['calendar.events.manage'],
      '/calendar/sync': ['calendar.events.manage'],
      '/calendar/holidays': ['calendar.events.view'],
      '/calendar/working-hours': ['calendar.events.manage'],

      // EzTask routes
      '/eztask': ['eztask.dashboard.view', 'eztask.boards.view'],
      '/eztask/dashboard': ['eztask.dashboard.view'],
      '/eztask/boards': ['eztask.boards.view'],
      '/eztask/board': ['eztask.boards.view'],

      // CNS routes
      '/cns': ['cns.dashboard.view'],
      '/cns/dashboard': ['cns.dashboard.view'],
      '/cns/content': ['cns.posts.create'],
      '/cns/scheduler': ['cns.posts.schedule'],
      '/cns/calendar': ['cns.posts.schedule'],
      '/cns/channels': ['cns.accounts.view'],
      '/cns/campaigns': ['cns.accounts.view'],
      '/cns/analytics': ['cns.analytics.view'],
      '/cns/email': ['cns.accounts.view'],
      '/cns/ads': ['cns.accounts.view'],
      '/cns/settings': ['cns.accounts.manage'],
      '/cns/ai': ['cns.accounts.manage'],

      // Report Builder routes
      '/reports': ['reports.dashboard.view', 'reports.reports.view'],
      '/reports/new': ['reports.reports.create'],
      '/reports/edit': ['reports.reports.create'],
      '/reports/preview': ['reports.reports.view'],
      '/reports/types': ['reports.reports.view'],
      '/reports/permissions': ['reports.reports.view'],
      '/reports/schedules': ['reports.schedule'],
      '/reports/schedule': ['reports.schedule'],

      // Human Resources routes
      '/hr': ['hr.dashboard.view'],
      '/hr/dashboard': ['hr.dashboard.view'],
      '/hr/employees': ['hr.employees.view'],
      '/hr/time-attendance': ['hr.time_attendance.view'],
      '/hr/leave': ['hr.leave.view'],
      '/hr/payroll': ['hr.payroll.view'],
      '/hr/pay-components': ['hr.pay_components.view'],
      '/hr/shifts': ['hr.shifts.view'],
      '/hr/positions': ['hr.positions.view'],

      // Accounting routes
      '/accounting': ['accounting.dashboard.view'],
      '/accounting/dashboard': ['accounting.dashboard.view'],
      '/accounting/chart-of-accounts': ['accounting.chart_of_accounts.view'],
      '/accounting/journals': ['accounting.journals.view'],
      '/accounting/events': ['accounting.events.view'],
      '/accounting/periods': ['accounting.periods.view'],
      '/accounting/exports': ['accounting.exports.view'],
      '/accounting/posting-rules': ['accounting.posting_rules.view'],

      // Fiscal routes
      '/fiscal': ['fiscal.dashboard.view'],
      '/fiscal/dashboard': ['fiscal.dashboard.view'],
      '/fiscal/documents': ['fiscal.documents.view'],
      '/fiscal/apuracao': ['fiscal.apuracao.view'],
      '/fiscal/sped': ['fiscal.sped.view'],
      '/fiscal/matrix': ['fiscal.matrix.view'],
      '/fiscal/events': ['fiscal.events.view'],
      '/fiscal/audit': ['fiscal.audit.view'],

      // EzParcel routes
      '/ezparcel': ['ezparcel.dashboard.view'],
      '/ezparcel/dashboard': ['ezparcel.dashboard.view'],
      '/ezparcel/shipments': ['ezparcel.shipments.view'],
      '/ezparcel/routes': ['ezparcel.routes.view'],
      '/ezparcel/drivers': ['ezparcel.drivers.view'],
      '/ezparcel/fleet': ['ezparcel.routes.view'],
      '/ezparcel/vehicles': ['ezparcel.routes.view'],
      '/ezparcel/warehouses': ['ezparcel.dashboard.view'],
      '/ezparcel/customers': ['ezparcel.dashboard.view'],
      '/ezparcel/billing': ['ezparcel.dashboard.view'],
      '/ezparcel/reports': ['ezparcel.reports.view', 'ezparcel.dashboard.view'],
      '/ezparcel/documents': ['ezparcel.dashboard.view'],
      '/ezparcel/notifications': ['ezparcel.dashboard.view'],
      '/ezparcel/admin': ['ezparcel.dashboard.view'],
      '/ezparcel/settings': ['ezparcel.dashboard.view'],
      '/ezparcel/integrations': ['ezparcel.dashboard.view'],
      '/ezparcel/communications': ['ezparcel.dashboard.view'],
      '/ezparcel/coldchain': ['coldchain.dashboard.view'],
      '/ezparcel/trails': ['trails.dashboard.view'],

      // Driver routes
      '/driver': ['driver.dashboard.view'],
      '/driver/route-load': ['driver.route.start'],
      '/driver/route-active': ['driver.route.start'],
      '/driver/stop-deliver': ['driver.stop.deliver'],

      // Mobile routes
      '/mobile': ['mobile.dashboard.view'],
      '/mobile/receiving': ['mobile.receiving.execute'],
      '/mobile/picking': ['mobile.picking.execute'],
      '/mobile/packing': ['mobile.packing.execute']
    };

    // Find matching route
    const routeKey = Object.keys(routePermissionMap)
      .sort((a, b) => b.length - a.length)
      .find((key) => route === key || route.startsWith(key + '/'));

    if (!routeKey) return false;

    const requiredPermissions = routePermissionMap[routeKey];
    return hasAnyPermission(requiredPermissions);
  }, [hasAnyPermission]);

  const getDefaultAllowedRoute = useCallback(() => {
    if (userRole === 'admin') return '/erp';

    const candidates = [
      ['/crm/campaign-intelligence', ['crm.campaign_intelligence.view', 'crm.dashboard.view', 'crm.leads.view']],
      ['/crm/dashboard', ['crm.dashboard.view']],
      ['/crm/leads', ['crm.leads.view']],
      ['/crm/accounts', ['crm.accounts.view']],
      ['/crm/opportunities', ['crm.opportunities.view']],
      ['/crm/quotes', ['crm.quotes.view']],
      ['/wms/dashboard', ['wms.dashboard.view']],
      ['/finance/dashboard', ['finance.dashboard.view']],
      ['/procurement/dashboard', ['procurement.dashboard.view']],
      ['/ecommerce/dashboard', ['ecommerce.dashboard.view']],
      ['/communications/dashboard', ['communications.dashboard.view']],
      ['/oig/dashboard', ['oig.dashboard.view']],
      ['/calendar', ['calendar.dashboard.view']],
      ['/eztask', ['eztask.dashboard.view', 'eztask.boards.view']],
      ['/cns/dashboard', ['cns.dashboard.view']],
      ['/reports', ['reports.dashboard.view', 'reports.reports.view']],
      ['/hr/dashboard', ['hr.dashboard.view']],
      ['/accounting/dashboard', ['accounting.dashboard.view']],
      ['/fiscal/dashboard', ['fiscal.dashboard.view']],
      ['/ezparcel/dashboard', ['ezparcel.dashboard.view']]
    ];

    const match = candidates.find(([, requiredPermissions]) => hasAnyPermission(requiredPermissions));
    if (!match && hasModulePermission('crm')) return '/crm/campaign-intelligence';
    return match ? match[0] : '/access-denied';
  }, [hasAnyPermission, hasModulePermission, userRole]);

  // Refresh permissions
  const refreshPermissions = useCallback(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const value = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasModulePermission,
    getModulePermissions,
    canAccessRoute,
    getDefaultAllowedRoute,
    refreshPermissions,
    isAdmin: userRole === 'admin',
    isManager: userRole === 'manager' || userRole === 'admin',
    isOperator: ['operator', 'manager', 'admin'].includes(userRole),
    loading: permissions.loading
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>);

};

// Role-based fallback permissions
function getRoleFallbackPermissions(role) {
  const viewPermissions = [
  'wms.dashboard.view', 'wms.inventory.view', 'wms.receiving.view',
  'wms.picking.view', 'wms.packing.view', 'wms.shipping.view',
  'finance.dashboard.view', 'procurement.dashboard.view',
  'crm.dashboard.view', 'crm.leads.view', 'crm.pipeline.view', 'crm.opportunities.view',
  'crm.quotes.view', 'crm.accounts.view', 'crm.prospect_discovery.view',
  'crm.campaign_intelligence.view', 'crm.marketing_hub.view', 'crm.portal_manager.view',
  'crm.orchestration.view', 'crm.publishing_console.view', 'crm.ad_connectors.view',
  'crm.presentation_hub.view', 'crm.dropship.view', 'crm.consignment.view',
  'crm.activities.view', 'crm.reports.view',
      'hr.dashboard.view', 'hr.employees.view', 'hr.time_attendance.view',
      'hr.leave.view', 'hr.payroll.view', 'hr.pay_components.view',
      'hr.shifts.view', 'hr.positions.view',
      'accounting.dashboard.view', 'accounting.chart_of_accounts.view',
      'accounting.journals.view', 'accounting.events.view', 'accounting.periods.view',
      'accounting.exports.view', 'accounting.posting_rules.view',
      'fiscal.dashboard.view', 'fiscal.documents.view', 'fiscal.apuracao.view',
      'fiscal.sped.view', 'fiscal.matrix.view', 'fiscal.events.view', 'fiscal.audit.view',
  'ezparcel.dashboard.view', 'ezparcel.shipments.view',
  'trails.dashboard.view', 'geofencing.dashboard.view', 'coldchain.dashboard.view'];


  const executePermissions = [
  'wms.receiving.execute', 'wms.putaway.execute', 'wms.picking.execute',
  'wms.packing.execute', 'wms.shipping.execute',
  'driver.route.start', 'driver.stop.deliver', 'driver.pod.signature',
  'mobile.receiving.execute', 'mobile.picking.execute', 'mobile.packing.execute',
  'fiscal.documents.execute', 'fiscal.apuracao.execute', 'fiscal.sped.execute'];


  const managePermissions = [
  'wms.inventory.edit', 'wms.inventory.adjust', 'wms.config.view',
  'ezparcel.shipments.create', 'ezparcel.routes.create', 'ezparcel.routes.assign',
  'finance.ledger.view', 'finance.payable.view', 'finance.receivable.view',
  'procurement.vendors.view', 'procurement.po.view', 'procurement.po.create'];


  const adminPermissions = [
  'admin.users.view', 'admin.users.create', 'admin.users.edit',
  'admin.profiles.view', 'admin.profiles.manage',
  'admin.system.view', 'admin.system.edit', 'admin.logs.view',
  'wms.config.edit', 'finance.payable.approve', 'procurement.po.approve'];


  switch (role) {
    case 'admin':
      return [...viewPermissions, ...executePermissions, ...managePermissions, ...adminPermissions];
    case 'manager':
      return [...viewPermissions, ...executePermissions, ...managePermissions];
    case 'operator':
      return [...viewPermissions, ...executePermissions];
    case 'viewer':
    default:
      return viewPermissions;
  }
}

function checkRoleFallback(role, capabilityCode) {
  if (!role) return false;

  // Admin has all
  if (role === 'admin') return true;

  // Check by capability pattern
  const isView = capabilityCode.includes('.view') || capabilityCode.includes('.dashboard');
  const isExecute = capabilityCode.includes('.execute');
  const isManage = capabilityCode.includes('.edit') || capabilityCode.includes('.create') || capabilityCode.includes('.manage');
  const isAdmin = capabilityCode.startsWith('admin.');
  const isApprove = capabilityCode.includes('.approve');

  switch (role) {
    case 'manager':
      return !isAdmin || !isApprove;
    case 'operator':
      return isView || isExecute;
    case 'viewer':
      return isView;
    default:
      return false;
  }
}

export default PermissionContext;
